import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';

// ─── Interfaces (from Concept Service OpenAPI) ─────────────────────────────────

export interface ConceptResponse {
  id: string;
  concept_id: string;
  app_id: string;
  title: string;
  prompt: string;
  languages: string[];
  tags: string[];
  is_active: boolean;
  generate_image: boolean;
  generate_body: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateConceptRequest {
  app_id: string;
  title: string;
  prompt: string;
  languages: string[];
  tags?: string[] | null;
  generate_image?: boolean;
  generate_body?: boolean;
}

export interface UpdateConceptRequest {
  title?: string | null;
  prompt?: string | null;
  languages?: string[] | null;
  tags?: string[] | null;
  is_active?: boolean | null;
  generate_image?: boolean | null;
  generate_body?: boolean | null;
}

export interface ListConceptsParams {
  app_id?: string | null;
  region?: string | null;
  is_active?: boolean | null;
  skip?: number;
  limit?: number;
}

// ─── Service ─────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root'
})
export class ConceptService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/concepts/`;

  /** GET /api/v1/concepts/ */
  list(params?: ListConceptsParams): Observable<ConceptResponse[]> {
    let url = this.baseUrl;
    if (params && Object.keys(params).length > 0) {
      const q = new URLSearchParams();
      if (params.app_id != null && params.app_id !== '') q.set('app_id', String(params.app_id));
      if (params.region != null && params.region !== '') q.set('region', String(params.region));
      if (params.is_active != null) q.set('is_active', String(params.is_active));
      if (params.skip != null) q.set('skip', String(params.skip));
      if (params.limit != null) q.set('limit', String(params.limit));
      const query = q.toString();
      if (query) url += '?' + query;
    }
    return this.http.get<ConceptResponse[]>(url);
  }

  /** POST /api/v1/concepts/ */
  create(payload: CreateConceptRequest): Observable<ConceptResponse> {
    return this.http.post<ConceptResponse>(this.baseUrl, payload);
  }

  /** GET /api/v1/concepts/{concept_id} */
  get(concept_id: string): Observable<ConceptResponse> {
    return this.http.get<ConceptResponse>(`${this.baseUrl}${concept_id}`);
  }

  /** PUT /api/v1/concepts/{concept_id} */
  update(concept_id: string, payload: UpdateConceptRequest): Observable<ConceptResponse> {
    return this.http.put<ConceptResponse>(`${this.baseUrl}${concept_id}`, payload);
  }

  /** DELETE /api/v1/concepts/{concept_id} */
  delete(concept_id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}${concept_id}`);
  }

  /** POST /api/v1/concepts/{concept_id}/test-zero-shot */
  testZeroShot(concept_id: string, language?: string): Observable<unknown> {
    const params = language ? `?language=${encodeURIComponent(language)}` : '';
    return this.http.post<unknown>(`${this.baseUrl}${concept_id}/test-zero-shot${params}`, {});
  }
}
